import { useEffect, useState } from "react";
import { Form, Link, useLoaderData, useSearchParams } from "react-router-dom";
import { MyDate } from "../common/functions";
import UserInfo from "../common/UserInfo";
import { TaskWorkOrder, workOrderStatusOptions } from "./types";

const workOrderStatusColorCode: Record<(typeof workOrderStatusOptions)[number], string> = {
    Queue: "#6c757d",
    Active: "#0d6efd",
    Parked: "#fd7e14",
    Reporting: "#6610f2",
    "Report Assist": "#d63384",
    Hold: "#dc3545",
    "Ready for Pickup": "#198754",
    Completed: "#146c43"
};

export default function Tasks(){
    const [searchParams, setSearchParams] = useSearchParams();
    const createdAtFrom = searchParams.get("createdAtFrom") ?? "";
    const createdAtTo = searchParams.get("createdAtTo") ?? "";
    const [initialCreatedAtFrom, setInitialCreatedAtFrom] = useState(createdAtFrom);
    const [initialCreatedAtTo, setInitialCreatedAtTo] = useState(createdAtTo);
    const filterWords = searchParams.get("filterWords") ?? "";
    const workOrderStatus = (searchParams.get("workOrderStatus") ?? "") as (typeof workOrderStatusOptions)[number] | "";
    const [refresh, setRefresh] = useState(true);
    const [timeStamp, setTimeStamp] = useState("");
    const legendStatuses: readonly (typeof workOrderStatusOptions)[number][] = workOrderStatus ? [workOrderStatus] : workOrderStatusOptions;

    const workOrders = useLoaderData() as TaskWorkOrder[];
    const filteredWorkOrders = workOrders.filter(
        workOrder =>
            JSON.stringify(workOrder).toLocaleLowerCase().includes(filterWords.toLocaleLowerCase()) &&
            (!workOrderStatus ? true : workOrder.workOrderStatus === workOrderStatus)
    );
    const techs = filteredWorkOrders
        .flatMap(item => item.techs)
        .filter((value, index, self) => self.findIndex(elem => elem.id === value.id) === index);
    const unassigned = filteredWorkOrders.filter(workOrder => !workOrder.techs ||workOrder.techs.length === 0);
    const unchanged = initialCreatedAtFrom === createdAtFrom && initialCreatedAtTo === createdAtTo;

    useEffect(() => {
        setTimeStamp("Loaded at " + new MyDate().s() + " " + new MyDate().ss());
    }, [refresh]);

    return (
        <div className="py-2">
            <Form method="post">
                <span>Work Order Creation Date</span>
                <input
                    id="createdAtFrom"
                    type="date"
                    className="form-control inline ms-2"
                    value={createdAtFrom}
                    onChange={e => {
                        const value = e.currentTarget.value;
                        const params = new URLSearchParams(searchParams);
                        params.set("createdAtFrom", value);
                        setSearchParams(params, { replace: false });
                    }}
                    max={createdAtTo}
                />
                <span className = "ms-2">&mdash;</span>
                <input
                    id="createdAtTo"
                    type="date"
                    className="form-control inline ms-2"
                    value={createdAtTo}
                    onChange={e => {
                        const value = e.currentTarget.value;
                        const params = new URLSearchParams(searchParams);
                        params.set("createdAtTo", value);
                        setSearchParams(params, { replace: false });
                    }}
                    min={createdAtFrom}
                />
                <button
                    children={unchanged ? "Refresh" : "Load"}
                    type="submit"
                    className={"btn ms-3" + (unchanged ? " btn-success" : " btn-primary")}
                    onClick={() => {
                        setRefresh(prev => !prev);
                        setInitialCreatedAtFrom(createdAtFrom);
                        setInitialCreatedAtTo(createdAtTo);
                    }}
                />
                <span className="ms-3">{timeStamp}</span>
            </Form>
            <nav className="mt-2">
                <label children = "Work Order Status" htmlFor="workOrderStatus"/>
                <select
                    id="workOrderStatus"
                    className="form-control inline ms-2"
                    value={workOrderStatus}
                    onChange={e => {
                        const value = e.currentTarget.value as (typeof workOrderStatusOptions)[number] | "";
                        const params = new URLSearchParams(searchParams);
                        if(value) params.set("workOrderStatus", value);
                        else params.delete("workOrderStatus");
                        setSearchParams(params, { replace: false });
                    }}
                >
                    <option value = "">All</option>
                    {workOrderStatusOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                    ))}
                </select>
                <label children = "Search" htmlFor="search" className="ms-3"/>
                <input
                    type="text"
                    id="search"
                    value={filterWords}
                    onChange={e => {
                        const value = e.currentTarget.value;
                        const params = new URLSearchParams(searchParams);
                        if (value) params.set("filterWords", value);
                        else params.delete("filterWords");
                        setSearchParams(params, { replace: false });
                    }}
                    className="form-control inline ms-2"
                />
            </nav>
            <div>
                <form autoComplete="off">
                    <div
                        className="mt-3 text-center font-weight-bold blk5"
                        style={{ fontSize: "calc(0.5vw + 5px)", display: "flex", justifyContent: "center" }}
                    >
                        <div className="me-3 font-weight-bold mb-3">Work Order Status Legend</div>
                        <div className="mb-3">
                            {legendStatuses.map((statusOption, index) => (
                                <span
                                    key={statusOption}
                                    style={{
                                        color: workOrderStatusColorCode[statusOption],
                                        padding: 10,
                                        border: "1px solid black",
                                        borderLeft: index === 0 ? "1px solid black" : "none"
                                    }}
                                >
                                    {statusOption} ({filteredWorkOrders.filter(v => v.workOrderStatus === statusOption).length})
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="table-responsive">
                        <table className="table">
                            <thead>
                                <tr className="noBorderTop">
                                    {techs.map(tech => (
                                        <th key={tech.id}>
                                            <UserInfo id={tech.id} name={tech.name} />
                                        </th>
                                    ))}
                                    {unassigned.length ? <th>Unassigned</th> : null}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredWorkOrders.length ? (
                                    <tr>
                                        {techs.map(tech => (
                                            <td key={tech.id}>
                                                <ul className="mb-0">
                                                    {filteredWorkOrders
                                                        .filter(workOrder => workOrder.techs?.some(elem => elem.id === tech.id))
                                                        .sort((a, b) => a.workOrderStatus.localeCompare(b.workOrderStatus))
                                                        .map(workOrder => (
                                                            <li key={workOrder.id}>
                                                                <Link to={`/WorkOrders/${String(workOrder.id)}`}>
                                                                    <span id={String(workOrder.id)} style={{ color: workOrderStatusColorCode[workOrder.workOrderStatus] }}>
                                                                        {workOrder.labNumber} - <strong>{workOrder.organizationName}</strong> ({workOrder.workOrderStatus})
                                                                    </span>
                                                                </Link>
                                                            </li>
                                                        ))}
                                                </ul>
                                            </td>
                                        ))}
                                        {unassigned.length ? (
                                            <td>
                                                {unassigned
                                                    .sort((a, b) => a.workOrderStatus.localeCompare(b.workOrderStatus))
                                                    .map(workOrder => (
                                                        <li key={workOrder.id}>
                                                            <Link to={`/WorkOrders/${String(workOrder.id)}`}>
                                                                <span id={String(workOrder.id)} style={{ color: workOrderStatusColorCode[workOrder.workOrderStatus] }}>
                                                                    {workOrder.labNumber} - <strong>{workOrder.organizationName}</strong> ({workOrder.workOrderStatus})
                                                                </span>
                                                            </Link>
                                                        </li>
                                                    ))}
                                            </td>
                                        ) : null}
                                    </tr>
                                ) : (
                                    <tr>
                                        <td className="noborder loading" style={{ backgroundColor: "white" }} colSpan={100}>
                                            No Task Yet
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </form>
            </div>
        </div>
    );
}
